// ============================================================================
// SERVICE: NotificationService.js — v1.0 — Passo 35
// Central de Notificações: Firestore + Web Push API
// Coleção compartilhada: artifacts/${appId}/public/data/office_notifications
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc, where, writeBatch } from 'firebase/firestore';

import { db } from '../shared/api/firebase/config';
import { FS } from '../shared/constants/firestorePaths';
import { FIREBASE_CONFIG } from '../config/Config';

const appId = FIREBASE_CONFIG.projectId;
const OFFICE_NOTIF_PATH = () => collection(db, ...FS.officeNotifications(appId));

// ── WRITE ──────────────────────────────────────────────────────────────────

export async function createOfficeNotification(title, message, type = 'SYSTEM', linkTo = null) {
  if (!appId) return null;
  try {
    const ref = OFFICE_NOTIF_PATH();
    const docRef = await addDoc(ref, {
      title:     String(title   || '').slice(0, 160),
      message:   String(message || '').slice(0, 500),
      type:      String(type    || 'SYSTEM'),
      linkTo:    linkTo || null,
      read:      false,
      createdAt: Date.now(),
    });
    return docRef.id;
  } catch {
    return null;
  }
}

export async function markOfficeNotificationRead(notificationId) {
  const nid = String(notificationId || '').trim();
  if (!nid || !appId) return;
  try {
    await updateDoc(doc(OFFICE_NOTIF_PATH(), nid), { read: true });
  } catch {}
}

export async function markAllOfficeNotificationsRead(notificationIds) {
  if (!appId || !Array.isArray(notificationIds) || !notificationIds.length) return;
  try {
    const batch = writeBatch(db);
    for (const nid of notificationIds) {
      batch.update(doc(OFFICE_NOTIF_PATH(), nid), { read: true });
    }
    await batch.commit();
  } catch {}
}

// ── BROWSER PUSH API ───────────────────────────────────────────────────────

export async function requestBrowserNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied')  return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

export function triggerBrowserNotification(title, message) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(String(title || 'RDA — Escritório').slice(0, 80), {
      body: String(message || '').slice(0, 200),
      icon: '/favicon.ico',
      tag:  `rda-${Date.now()}`,
    });
  } catch {}
}

// ── HOOK ───────────────────────────────────────────────────────────────────

export function useOfficeNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const isFirstLoad = useRef(true);
  const prevIdsRef  = useRef(new Set());

  useEffect(() => {
    if (!appId) return;
    setLoading(true);

    const q = query(
      OFFICE_NOTIF_PATH(),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setNotifications(items);
      setLoading(false);

      if (!isFirstLoad.current) {
        for (const item of items) {
          if (!prevIdsRef.current.has(item.id)) {
            triggerBrowserNotification(item.title, item.message);
          }
        }
      }
      isFirstLoad.current = false;
      prevIdsRef.current  = new Set(items.map((i) => i.id));
    }, () => { setLoading(false); });

    return () => unsub();
  }, []);

  return { notifications, unreadCount: notifications.length, loading };
}

// ── useClientNotifications ─────────────────────────────────────────────────
// Reacts to client-specific notifications (path: clients/{clientId}/notifications).
// Fires browser push on new SIGNATURE_REQUEST entries (MC2).
export function useClientNotifications(clientId) {
  const [notifications, setNotifications] = useState([]);
  const isFirstLoad = useRef(true);
  const prevIdsRef  = useRef(new Set());

  useEffect(() => {
    const cid = String(clientId || '').trim();
    if (!cid || !appId) { setNotifications([]); return; }

    const col = collection(db, ...FS.clientNotifications(appId, cid));
    const q   = query(col, where('read', '==', false), orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setNotifications(items);

      if (!isFirstLoad.current) {
        for (const item of items) {
          if (!prevIdsRef.current.has(item.id)) {
            triggerBrowserNotification(
              String(item.title   || 'Escritório RDA'),
              String(item.message || '')
            );
          }
        }
      }
      isFirstLoad.current = false;
      prevIdsRef.current  = new Set(items.map((i) => i.id));
    }, () => { setNotifications([]); });

    return () => unsub();
  }, [clientId]);

  return {
    notifications,
    signaturePendingCount: notifications.filter((n) => n.type === 'SIGNATURE_REQUEST').length,
  };
}
